/**
 * Aircraft markers, driven straight from the store into the DOM.
 *
 * This is the hot path of the whole application and it contains no React.
 * `subscribeToDrone` calls back with one drone when that drone changes, and the
 * callback writes `marker.setLngLat` and a couple of element attributes. A
 * ten-aircraft fleet at 10 Hz costs a hundred callbacks a second and zero
 * renders.
 *
 * Creation and update are the same path. There is one function that applies a
 * drone to a marker, called once when the marker is made and again on every
 * change. The predecessor's attempt at this kept a React rendering path
 * alongside the imperative one, gated on a `mapLoaded` state flag that never
 * became true under test, so the imperative path never ran, the React path
 * shadowed it, and nobody could tell which was drawing. Hence `stats()`: the
 * number of applied position updates is observable, and a test asserts it is
 * not zero.
 */

import { createMapMarker, geoJsonSource, useMapController, type MapLike } from '@/map/MapCanvas';
import {
  applyMarkerHeading,
  applyMarkerState,
  createDroneMarkerElement,
  trackColorFor,
  type DroneMarkerElement,
} from '@/map/icons';
import { isRepositioning } from '@/map/repositioning';
import { subscribeToDrone, subscribeToRoster, useDroneStore } from '@/stores/droneStore';
import { useMapStore } from '@/stores/mapStore';
import type { Drone, DroneId } from '@/types/domain';
import { useEffect, useRef } from 'react';

export const TRACK_SOURCE_ID = 'drone-tracks';
export const TRACK_LAYER_ID = 'drone-tracks-line';

/** How many track points are kept per aircraft. At 10 Hz this is about a minute. */
const DEFAULT_TRACK_POINTS = 600;

/**
 * How often the track line is rebuilt.
 *
 * Markers move on every fix; the trail does not need to. Rebuilding a
 * FeatureCollection of ten 600-point lines at 10 Hz is 6,000 coordinate pairs
 * serialised per tick for a change of one pixel at the head of each line.
 */
const TRACK_REDRAW_MS = 250;

/**
 * Position steps below this are treated as GPS jitter and not added to the
 * track. A stationary aircraft walks a metre or two per fix, which at 5 Hz drew
 * a scribble over the launch point and, in the predecessor's flight log,
 * accumulated kilometres of "distance flown" during a hover.
 */
const MIN_TRACK_STEP_M = 1.0;

export interface MarkerLike {
  setLngLat(lngLat: [number, number]): void;
  remove(): void;
}

export type MarkerFactory = (
  map: MapLike,
  element: HTMLElement,
  lngLat: [number, number],
) => MarkerLike;

export interface DroneMarkerStats {
  markers: number;
  /** Position writes actually applied to a marker. The proof the fast path runs. */
  positionUpdates: number;
  trackRedraws: number;
  /** Updates skipped because the coordinate had not moved. */
  unchanged: number;
  /** Updates skipped because the drone reported no usable fix. */
  noFix: number;
}

export interface DroneMarkerLayerOptions {
  createMarker?: MarkerFactory;
  /** Where a marker sits, hidden, until its aircraft reports a fix. */
  fallbackCenter?: [number, number];
  /** Team colour for a drone, or null to use the per-drone default. */
  colorFor?: (droneId: DroneId) => string | null;
  onSelect?: (droneId: DroneId) => void;
  trackPoints?: number;
  trackRedrawMs?: number;
  now?: () => number;
}

export interface DroneMarkerLayer {
  destroy: () => void;
  stats: () => DroneMarkerStats;
  /** Re-read colours after the team map arrives, without touching positions. */
  refresh: () => void;
  /**
   * Hide or show the aircraft and their trails, for the LAYERS switch.
   *
   * Hidden, not destroyed: the store subscriptions keep running, so an aircraft
   * that moves or is lost while the layer is off is in the right place the
   * instant it comes back rather than snapping there a second later.
   */
  setVisible: (visible: boolean) => void;
}

interface MarkerEntry {
  marker: MarkerLike;
  element: DroneMarkerElement;
  unsubscribe: () => void;
  lat: number | null;
  lon: number | null;
  track: Array<[number, number]>;
  color: string;
}

const defaultMarkerFactory: MarkerFactory = createMapMarker;

/**
 * A reported position of exactly 0,0 is not a position.
 *
 * ArduPilot sends GLOBAL_POSITION_INT with lat and lon zero before it has a
 * fix, and the predecessor drew every powered-on aircraft in the Gulf of Guinea
 * until GPS locked, which on a cold start is up to a minute of the fleet
 * apparently sitting off the coast of Africa.
 */
function hasFix(drone: Drone): boolean {
  const { lat, lon } = drone.scalars;
  if (lat === undefined || lon === undefined) return false;
  if (lat === 0 && lon === 0) return false;
  return Number.isFinite(lat) && Number.isFinite(lon);
}

function metresBetween(
  latA: number,
  lonA: number,
  latB: number,
  lonB: number,
): number {
  // Equirectangular approximation. Over the tens of metres between consecutive
  // fixes it is within a centimetre of the haversine and costs three trig calls
  // fewer, which matters at fleet rate.
  const meanLat = ((latA + latB) / 2) * (Math.PI / 180);
  const dLat = (latB - latA) * 111_320;
  const dLon = (lonB - lonA) * 111_320 * Math.cos(meanLat);
  return Math.hypot(dLat, dLon);
}

export function createDroneMarkerLayer(
  map: MapLike,
  options: DroneMarkerLayerOptions = {},
): DroneMarkerLayer {
  const createMarker = options.createMarker ?? defaultMarkerFactory;
  const now = options.now ?? Date.now;
  const trackPoints = options.trackPoints ?? DEFAULT_TRACK_POINTS;
  const trackRedrawMs = options.trackRedrawMs ?? TRACK_REDRAW_MS;

  const entries = new Map<DroneId, MarkerEntry>();
  let hidden = false;
  const stats: DroneMarkerStats = {
    markers: 0,
    positionUpdates: 0,
    trackRedraws: 0,
    unchanged: 0,
    noFix: 0,
  };

  let lastTrackRedraw = 0;
  let trackTimer: ReturnType<typeof setTimeout> | null = null;
  let destroyed = false;

  if (!map.getSource(TRACK_SOURCE_ID)) {
    map.addSource(TRACK_SOURCE_ID, {
      type: 'geojson',
      data: { type: 'FeatureCollection', features: [] },
    });
  }
  if (!map.getLayer(TRACK_LAYER_ID)) {
    map.addLayer({
      id: TRACK_LAYER_ID,
      type: 'line',
      source: TRACK_SOURCE_ID,
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': ['get', 'color'],
        'line-width': 2,
        'line-opacity': 0.75,
      },
    });
  }

  function redrawTracks(): void {
    if (destroyed) return;
    const source = geoJsonSource(map, TRACK_SOURCE_ID);
    if (!source) return;

    const features = [];
    for (const [droneId, entry] of entries) {
      if (entry.track.length < 2) continue;
      features.push({
        type: 'Feature',
        properties: { droneId, color: entry.color },
        geometry: { type: 'LineString', coordinates: entry.track },
      });
    }
    source.setData({ type: 'FeatureCollection', features });
    lastTrackRedraw = now();
    stats.trackRedraws += 1;
  }

  function scheduleTrackRedraw(): void {
    if (trackTimer !== null || destroyed) return;
    const elapsed = now() - lastTrackRedraw;
    if (elapsed >= trackRedrawMs) {
      redrawTracks();
      return;
    }
    trackTimer = setTimeout(() => {
      trackTimer = null;
      redrawTracks();
    }, trackRedrawMs - elapsed);
  }

  function colorOf(droneId: DroneId): string {
    return options.colorFor?.(droneId) ?? trackColorFor(droneId);
  }

  /**
   * The single apply path. Called on marker creation and on every store change
   * for that drone, so there is no "first render" behaviour to diverge from
   * steady state.
   */
  function apply(droneId: DroneId, entry: MarkerEntry, drone: Drone | undefined): void {
    if (!drone) return;

    applyMarkerState(entry.element, {
      connection: drone.state,
      armed: drone.scalars.armed === true,
      selected: useDroneStore.getState().selectedIds.includes(droneId),
      batteryPercent: drone.scalars.batteryPercent,
      color: options.colorFor?.(droneId) ?? null,
    });
    applyMarkerHeading(entry.element, drone.scalars.heading ?? null);

    if (!hasFix(drone)) {
      stats.noFix += 1;
      return;
    }

    const lat = drone.scalars.lat as number;
    const lon = drone.scalars.lon as number;
    if (lat === entry.lat && lon === entry.lon) {
      stats.unchanged += 1;
      return;
    }

    const previousLat = entry.lat;
    const previousLon = entry.lon;
    entry.lat = lat;
    entry.lon = lon;
    entry.marker.setLngLat([lon, lat]);
    entry.element.root.dataset.fix = 'ok';
    stats.positionUpdates += 1;

    // A simulator being dragged is being placed, not flown. Recording those
    // positions drew a long trail across the map for a journey nothing made —
    // and the trail outlived the drag, so the map claimed a flight had happened.
    // The existing history is dropped too: a track that jumps from where the
    // aircraft was to where it was put down is a line through ground it never
    // crossed.
    if (isRepositioning(droneId)) {
      if (entry.track.length) {
        entry.track.length = 0;
        scheduleTrackRedraw();
      }
      return;
    }

    const moved =
      previousLat === null || previousLon === null
        ? Infinity
        : metresBetween(previousLat, previousLon, lat, lon);
    if (moved >= MIN_TRACK_STEP_M) {
      entry.track.push([lon, lat]);
      if (entry.track.length > trackPoints) entry.track.shift();
      scheduleTrackRedraw();
    }
  }

  function addDrone(droneId: DroneId): void {
    if (entries.has(droneId)) return;
    const drone = useDroneStore.getState().drones.get(droneId);
    const element = createDroneMarkerElement(drone?.callsign ?? droneId);
    // Which aircraft this marker is, readable from a DOM event target. The
    // planning screen's drag handler needs to know what it grabbed.
    element.root.dataset.droneId = droneId;

    element.root.addEventListener('click', (event) => {
      event.stopPropagation();
      if (options.onSelect) options.onSelect(droneId);
      else useDroneStore.getState().select(droneId);
    });

    // A marker must be positioned before it goes on the map. Until the aircraft
    // reports a fix it sits at the map centre and stays hidden, so an airframe
    // with no GPS is absent from the map rather than drawn somewhere arbitrary.
    const fix = drone && hasFix(drone) ? drone.scalars : null;
    const initial: [number, number] = fix
      ? [fix.lon as number, fix.lat as number]
      : [options.fallbackCenter?.[0] ?? 0, options.fallbackCenter?.[1] ?? 0];

    const entry: MarkerEntry = {
      marker: createMarker(map, element.root, initial),
      element,
      unsubscribe: () => {},
      lat: null,
      lon: null,
      track: [],
      color: colorOf(droneId),
    };
    if (hidden) element.root.style.display = 'none';
    entries.set(droneId, entry);
    stats.markers = entries.size;

    entry.unsubscribe = subscribeToDrone(droneId, (next) => apply(droneId, entry, next));
  }

  function removeDrone(droneId: DroneId): void {
    const entry = entries.get(droneId);
    if (!entry) return;
    entry.unsubscribe();
    entry.marker.remove();
    entries.delete(droneId);
    stats.markers = entries.size;
    scheduleTrackRedraw();
  }

  const unsubscribeRoster = subscribeToRoster((ids) => {
    const wanted = new Set(ids);
    for (const droneId of [...entries.keys()]) {
      if (!wanted.has(droneId)) removeDrone(droneId);
    }
    for (const droneId of ids) addDrone(droneId);
  });

  // Selection is not telemetry, so it gets its own subscription rather than
  // riding along on every position fix.
  let previousSelection = useDroneStore.getState().selectedIds;
  const unsubscribeSelection = useDroneStore.subscribe((state) => {
    if (state.selectedIds === previousSelection) return;
    previousSelection = state.selectedIds;
    for (const [droneId, entry] of entries) {
      entry.element.root.dataset.selected = previousSelection.includes(droneId)
        ? 'true'
        : 'false';
    }
  });

  return {
    destroy: () => {
      destroyed = true;
      if (trackTimer !== null) clearTimeout(trackTimer);
      unsubscribeRoster();
      unsubscribeSelection();
      for (const droneId of [...entries.keys()]) removeDrone(droneId);
      if (map.getLayer(TRACK_LAYER_ID)) map.removeLayer(TRACK_LAYER_ID);
      if (map.getSource(TRACK_SOURCE_ID)) map.removeSource(TRACK_SOURCE_ID);
    },

    stats: () => ({ ...stats }),

    refresh: () => {
      for (const [droneId, entry] of entries) {
        entry.color = colorOf(droneId);
        apply(droneId, entry, useDroneStore.getState().drones.get(droneId));
      }
      redrawTracks();
    },

    setVisible: (visible: boolean) => {
      for (const entry of entries.values()) {
        entry.element.root.style.display = visible ? '' : 'none';
      }
      // Markers created while the layer is off have to start hidden too, or the
      // next aircraft to connect appears on a map the operator switched off.
      hidden = !visible;
      if (map.getLayer(TRACK_LAYER_ID)) {
        map.setLayoutProperty(TRACK_LAYER_ID, 'visibility', visible ? 'visible' : 'none');
      }
    },
  };
}

/**
 * Mount the marker layer on the surrounding MapCanvas.
 *
 * The returned ref holds the layer so a screen can read `stats()`. It is not
 * state and reading it does not re-render anything.
 */
export function useDroneMarkers(
  options: Omit<DroneMarkerLayerOptions, 'createMarker'> = {},
): { current: DroneMarkerLayer | null } {
  const controller = useMapController();
  const layerRef = useRef<DroneMarkerLayer | null>(null);
  const visible = useMapStore((state) => state.layers.drones);
  // Read through a ref inside the mount effect so mounting does not depend on
  // the switch: the map becomes ready asynchronously, and a layer built after
  // the operator had already switched aircraft off would otherwise come up
  // visible.
  const visibleRef = useRef(visible);
  visibleRef.current = visible;

  // Options are read through a ref so a screen re-render, which produces a new
  // `colorFor` closure every time, cannot tear down and rebuild every marker.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(
    () =>
      controller.ready((map) => {
        const layer = createDroneMarkerLayer(map, {
          colorFor: (id) => optionsRef.current.colorFor?.(id) ?? null,
          onSelect: (id) => {
            const handler = optionsRef.current.onSelect;
            if (handler) handler(id);
            else useDroneStore.getState().select(id);
          },
          ...(optionsRef.current.trackPoints !== undefined
            ? { trackPoints: optionsRef.current.trackPoints }
            : {}),
        });
        layer.setVisible(visibleRef.current);
        layerRef.current = layer;
        return () => {
          layerRef.current = null;
          layer.destroy();
        };
      }),
    [controller],
  );

  // Separate from the mount effect so flipping the switch does not tear the
  // markers down and rebuild them, which would drop every trail.
  useEffect(() => {
    layerRef.current?.setVisible(visible);
  }, [visible]);

  return layerRef;
}
